/**
 * Shared Dashboard Widgets
 * Used by both ProjectSummaryPage (interactive) and ReportPreview (compact/static).
 * Pass compact={true} to get print-ready sizing with no interactive elements.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  BarChart3, TrendingUp, TrendingDown, Users, MessageSquare,
  Lightbulb, CheckCircle2, Clock, ShieldAlert, Sparkles, Zap,
  RefreshCw, ArrowUp, ArrowDown, SlidersHorizontal, Type, Hash, Eye,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import api from '../../lib/api';

/* ─── Utility ──────────────────────────────────────────── */
export function formatScore(score, denominator = 10) {
  if (score === null || score === undefined) return '-';
  const n = parseFloat(score);
  if (isNaN(n)) return '-';
  return denominator <= 10 ? n.toFixed(1) : Math.round(n).toString();
}

const COLORS = ['#5AA2AE', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#ec4899', '#06b6d4'];

/* ═══════════════════════════════════════════════════════════
   SegmentBreakdownChart
   ═══════════════════════════════════════════════════════════ */
export function SegmentBreakdownChart({
  segmentType,
  onSegmentClick,
  selectedSegment,
  anonymityThreshold = 3,
  compact = false,
}) {
  const segments = segmentType.segments || [];
  const total = segmentType.total_unique || segments.reduce((sum, s) => sum + s.total, 0);
  const sortedSegments = [...segments].sort((a, b) => b.total - a.total);
  const maxShow = compact ? 8 : 6;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className={`font-medium ${compact ? 'text-[11px]' : 'text-sm'} text-slate-700`}>{segmentType.name}</h4>
        <span className={`${compact ? 'text-[9px]' : 'text-xs'} text-slate-400`}>{total} stakeholders</span>
      </div>

      <div className={compact ? 'space-y-1' : 'space-y-2'}>
        {sortedSegments.slice(0, maxShow).map((seg) => {
          const completionRate = seg.invited > 0 ? Math.round((seg.completed / seg.invited) * 100) : 0;
          const belowThreshold = seg.completed < anonymityThreshold;
          const isSelected = !compact && selectedSegment?.segment_id === seg.segment_id;

          return (
            <div
              key={seg.segment_id}
              className={`${compact ? '' : 'group cursor-pointer rounded-lg p-1 -mx-1 transition-all'} ${isSelected ? 'bg-[#5AA2AE]/10 ring-1 ring-[#5AA2AE]' : compact ? '' : 'hover:bg-slate-50'}`}
              onClick={compact ? undefined : () => onSegmentClick?.(seg)}
              data-testid={compact ? undefined : `segment-filter-${seg.segment_id}`}
            >
              <div className={`flex items-center justify-between ${compact ? 'text-[10px] mb-0.5' : 'text-xs mb-1'}`}>
                <span className={`font-medium transition-colors ${isSelected ? 'text-[#5AA2AE]' : 'text-slate-600'} ${!compact && !isSelected ? 'group-hover:text-[#5AA2AE]' : ''}`}>
                  {seg.segment_name}
                  {isSelected && <span className="ml-1 text-[10px]">(click to clear)</span>}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">
                    {seg.invited}<span className="text-slate-300">/</span>{seg.reference}
                  </span>
                  <span className={`font-medium ${completionRate >= 70 ? 'text-emerald-600' : completionRate >= 40 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {completionRate}%
                  </span>
                  {!compact && belowThreshold && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Below anonymity threshold ({seg.completed}/{anonymityThreshold} responses)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>

              <div className={`flex ${compact ? 'h-4' : 'h-5'} rounded-md overflow-hidden bg-slate-100`}>
                <div
                  className={`h-full ${isSelected ? 'bg-emerald-600' : 'bg-emerald-500'} ${!compact ? 'group-hover:bg-emerald-600 transition-all' : ''}`}
                  style={{ width: `${total > 0 ? (seg.completed / total * 100) * 3 : 0}%`, minWidth: seg.completed > 0 ? (compact ? 3 : 4) + 'px' : 0 }}
                />
                <div
                  className={`h-full ${isSelected ? 'bg-[#4a8a94]' : 'bg-[#5AA2AE]'} ${!compact ? 'group-hover:bg-[#4a8a94] transition-all' : ''}`}
                  style={{ width: `${total > 0 ? ((seg.invited - seg.completed) / total * 100) * 3 : 0}%`, minWidth: (seg.invited - seg.completed) > 0 ? (compact ? 3 : 4) + 'px' : 0 }}
                />
                <div
                  className={`h-full ${isSelected ? 'bg-purple-500' : 'bg-purple-400'} ${!compact ? 'group-hover:bg-purple-500 transition-all' : ''}`}
                  style={{ width: `${total > 0 ? (seg.reference / total * 100) * 3 : 0}%`, minWidth: seg.reference > 0 ? (compact ? 3 : 4) + 'px' : 0 }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={`flex items-center gap-3 ${compact ? 'text-[9px] pt-1' : 'text-xs pt-2'} border-t flex-wrap`}>
        <div className="flex items-center gap-1"><div className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} rounded bg-emerald-500`} /><span className="text-slate-500">Completed</span></div>
        <div className="flex items-center gap-1"><div className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} rounded bg-[#5AA2AE]`} /><span className="text-slate-500">Pending</span></div>
        <div className="flex items-center gap-1"><div className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} rounded bg-purple-400`} /><span className="text-slate-500">Reference</span></div>
        <span className="text-slate-400">Inv/Ref &bull; Completion %</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DriverScoreGauge
   ═══════════════════════════════════════════════════════════ */
export function DriverScoreGauge({ driver, maxScore = 10, compact = false }) {
  const percentage = (driver.avg_score / maxScore) * 100;
  const color = driver.avg_score >= 7.5 ? '#22c55e' : driver.avg_score >= 6.5 ? '#84cc16' : driver.avg_score >= 5.5 ? '#fbbf24' : driver.avg_score >= 4 ? '#f97316' : '#ef4444';
  const size = compact ? 80 : 96;
  const r = compact ? 32 : 40;
  const sw = compact ? 7 : 8;

  return (
    <div className="text-center">
      <div className="relative mx-auto" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#e5e7eb" strokeWidth={sw} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={sw} fill="none"
            strokeDasharray={`${percentage * (2 * Math.PI * r / 100)} ${2 * Math.PI * r}`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${compact ? 'text-xl' : 'text-2xl'} font-bold`} style={{ color }}>{formatScore(driver.avg_score, 10)}</span>
        </div>
      </div>
      <p className={`${compact ? 'text-[11px] mt-1' : 'text-sm mt-2'} font-medium text-slate-700`}>{driver.driver_name}</p>
      <p className={`${compact ? 'text-[9px]' : 'text-xs'} text-slate-400`}>{driver.response_count} responses</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   KeyMetricsTile
   ═══════════════════════════════════════════════════════════ */
export function KeyMetricsTile({ topMetrics, bottomMetrics, loading, compact = false }) {
  if (loading && !compact) {
    return (
      <Card>
        <CardHeader className="pb-2"><Skeleton className="h-5 w-32" /></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
            <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const MetricRow = ({ metric }) => {
    const percentage = metric.scale_max ? (metric.avg / metric.scale_max * 100) : (metric.avg * 10);
    return (
      <div className={`flex items-center gap-${compact ? '2' : '3'} ${compact ? 'p-1' : 'p-2'} rounded-lg bg-slate-50 ${compact ? '' : 'hover:bg-slate-100 transition-colors'}`}>
        <div className="flex-1 min-w-0">
          <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-medium text-slate-700 truncate`}>{metric.heading}</p>
          <div className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'mt-0.5' : 'mt-1'}`}>
            <Progress value={percentage} className={`${compact ? 'h-1' : 'h-1.5'} flex-1`} />
            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-semibold text-slate-600 whitespace-nowrap`}>
              {formatScore(metric.avg, metric.scale_max || 10)}/{metric.scale_max || 10}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="key-metrics-tile">
      <CardHeader className={compact ? 'pb-1 px-3 pt-2' : 'pb-2'}>
        <CardTitle className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'text-[11px]' : 'text-base'}`}>
          <BarChart3 className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-[#5AA2AE]`} />
          Key Metrics
        </CardTitle>
        <CardDescription className={compact ? 'text-[9px]' : 'text-xs'}>
          {compact ? null : 'About Me question scores ranked by performance'}
        </CardDescription>
      </CardHeader>
      <CardContent className={compact ? 'px-3 pb-2' : undefined}>
        <div className={`grid grid-cols-2 ${compact ? 'gap-3' : 'gap-6'}`}>
          <div className={compact ? 'space-y-1' : 'space-y-2'}>
            <div className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'mb-1 pb-1' : 'mb-3 pb-2'} border-b`}>
              <ArrowUp className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
              <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-medium text-slate-600`}>Highest Scores</span>
            </div>
            {topMetrics && topMetrics.length > 0 ? (
              topMetrics.map((metric, i) => <MetricRow key={metric.question_id || i} metric={metric} />)
            ) : (
              <p className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-400 text-center py-4`}>No data</p>
            )}
          </div>
          <div className={compact ? 'space-y-1' : 'space-y-2'}>
            <div className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'mb-1 pb-1' : 'mb-3 pb-2'} border-b`}>
              <ArrowDown className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
              <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-medium text-slate-600`}>Lowest Scores</span>
            </div>
            {bottomMetrics && bottomMetrics.length > 0 ? (
              bottomMetrics.map((metric, i) => <MetricRow key={metric.question_id || i} metric={metric} />)
            ) : (
              <p className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-400 text-center py-4`}>No data</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════
   TrendAnalysisChart
   Accepts optional `trendData` prop for static rendering (report mode).
   If not provided, fetches data from API (dashboard mode).
   ═══════════════════════════════════════════════════════════ */
export function TrendAnalysisChart({
  projectId,
  segmentTypes,
  trendData: externalData,
  compact = false,
}) {
  const [trendData, setTrendData] = useState(externalData || null);
  const [loadingTrend, setLoadingTrend] = useState(!externalData);
  const [selectedSegmentType, setSelectedSegmentType] = useState('');

  useEffect(() => {
    if (externalData) return; // skip fetching when data is provided
    const fetchTrend = async () => {
      setLoadingTrend(true);
      try {
        const params = selectedSegmentType ? `?segment_type_id=${selectedSegmentType}` : '';
        const res = await api.get(`/projects/${projectId}/trend-analysis${params}`);
        setTrendData(res.data);
      } catch (err) {
        console.error('Error fetching trend:', err);
      } finally {
        setLoadingTrend(false);
      }
    };
    if (projectId) fetchTrend();
  }, [projectId, selectedSegmentType, externalData]);

  const chartData = trendData?.months?.map((month, i) => {
    const point = { month };
    trendData.series?.forEach(s => { point[s.name] = s.data[i]; });
    return point;
  }) || [];

  const questionName = trendData?.topline_question?.heading || 'Top-Line Score';
  const chartH = compact ? 160 : 250;
  const axisFontSize = compact ? 8 : 10;

  if (loadingTrend && !compact) {
    return (
      <Card data-testid="trend-analysis-chart">
        <CardHeader className="pb-2"><Skeleton className="h-5 w-40" /></CardHeader>
        <CardContent><Skeleton className="h-[250px]" /></CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="trend-analysis-chart">
      <CardHeader className={compact ? 'pb-1 px-3 pt-2' : 'pb-2'}>
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'text-[11px]' : 'text-base'}`}>
            <TrendingUp className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-[#5AA2AE]`} />
            Trend Analysis
          </CardTitle>
          {!compact && (
            <select
              value={selectedSegmentType}
              onChange={(e) => setSelectedSegmentType(e.target.value)}
              className="text-xs border rounded-md px-2 py-1 text-slate-600 bg-white"
              data-testid="trend-segment-select"
            >
              <option value="">Overall</option>
              {segmentTypes?.map(st => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </select>
          )}
          {compact && trendData?.series?.length > 0 && (
            <Badge variant="secondary" className="text-[8px] h-4">{selectedSegmentType ? '' : 'Overall'}</Badge>
          )}
        </div>
        <CardDescription className={compact ? 'text-[9px]' : 'text-xs'}>{questionName} over time</CardDescription>
      </CardHeader>
      <CardContent className={compact ? 'px-3 pb-2' : undefined}>
        {chartData.length > 0 && trendData?.series?.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={chartH}>
              <LineChart data={chartData} margin={{ top: 5, right: compact ? 5 : 10, left: compact ? -15 : -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: axisFontSize, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: axisFontSize, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <RechartsTooltip contentStyle={{ fontSize: compact ? 9 : 12, borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={(v) => v !== null ? [v.toFixed(1), ''] : ['-', '']} />
                {trendData.series.map((s) => (
                  <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={compact ? 1.5 : 2} dot={{ r: compact ? 2 : 3, fill: s.color }} activeDot={compact ? false : { r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
            {trendData.series.length > 1 && (
              <div className={`flex flex-wrap gap-x-${compact ? '3' : '4'} gap-y-${compact ? '0.5' : '1'} mt-${compact ? '1' : '2'} px-${compact ? '1' : '2'}`}>
                {trendData.series.map((s) => (
                  <div key={s.name} className="flex items-center gap-1.5">
                    <div className={`${compact ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-full`} style={{ backgroundColor: s.color }} />
                    <span className={`${compact ? 'text-[8px]' : 'text-[10px]'} text-slate-600`}>{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className={`flex items-center justify-center ${compact ? 'h-[160px]' : 'h-[250px]'} text-slate-400`}>
            <div className="text-center">
              <Clock className={`${compact ? 'w-4 h-4' : 'w-6 h-6'} mx-auto mb-2`} />
              <p className={compact ? 'text-[10px]' : 'text-sm'}>No trend data available yet</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════
   AIInsightsTile
   ═══════════════════════════════════════════════════════════ */
export function AIInsightsTile({ insights, loading, onRefresh, compact = false }) {
  if (loading && !compact) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2"><Skeleton className="h-5 w-5 rounded-full" /><Skeleton className="h-5 w-32" /></div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">{[1, 2, 3, 4].map(i => <div key={i} className="space-y-2"><Skeleton className="h-4 w-24" /><Skeleton className="h-16" /></div>)}</div>
        </CardContent>
      </Card>
    );
  }

  const InsightSection = ({ title, items, icon: Icon, colorClass }) => {
    if (!items || items.length === 0) return null;
    const stringItems = items.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        if (item.insight) return item.insight;
        if (item.action) return item.action;
        if (item.text) return item.text;
        if (item.segment_name || item.segment) {
          const segName = item.segment_name || item.segment;
          if (item.deviation !== undefined && typeof item.deviation === 'number') {
            return `${segName} scores ${Math.abs(item.deviation).toFixed(1)} points ${item.deviation > 0 ? 'higher' : 'lower'} than average`;
          }
          if (item.score !== undefined) return `${segName}: ${typeof item.score === 'number' ? item.score.toFixed(1) : item.score}`;
          return segName;
        }
        const vals = Object.values(item).filter(v => typeof v === 'string');
        return vals.length > 0 ? vals.join(' - ') : null;
      }
      return String(item);
    }).filter(Boolean);
    if (stringItems.length === 0) return null;
    const displayItems = compact ? stringItems.slice(0, 2) : stringItems;

    return (
      <div className={compact ? 'space-y-1' : 'space-y-2'}>
        <h4 className={`${compact ? 'text-[10px]' : 'text-xs'} font-semibold flex items-center gap-1 ${colorClass}`}>
          <Icon className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
          {title}
        </h4>
        <ul className={compact ? 'space-y-0.5' : 'space-y-1'}>
          {displayItems.map((item, i) => (
            <li key={i} className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-700 flex items-start gap-${compact ? '1.5' : '2'}`}>
              <span className={`mt-1 ${compact ? 'w-1 h-1' : 'w-1.5 h-1.5'} rounded-full flex-shrink-0 ${colorClass.replace('text-', 'bg-')}`} />
              <span className={compact ? 'leading-relaxed' : ''}>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200" data-testid="ai-insights-tile">
      <CardHeader className={compact ? 'pb-1 px-3 pt-2' : 'pb-2'}>
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-${compact ? '1' : '2'} ${compact ? 'text-[11px]' : 'text-base'}`}>
            <Sparkles className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-purple-600`} />
            AI Insights
          </CardTitle>
          {!compact && onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 px-2"><RefreshCw className="w-3 h-3" /></Button>
          )}
        </div>
        <CardDescription className={compact ? 'text-[9px]' : 'text-xs'}>
          {compact ? null : 'Data-driven analysis for executive decision-making'}
        </CardDescription>
      </CardHeader>
      <CardContent className={compact ? 'px-3 pb-2' : undefined}>
        {insights ? (
          <div className={`grid grid-cols-2 ${compact ? 'gap-x-4 gap-y-2' : 'gap-x-6 gap-y-4'}`}>
            <InsightSection title="Strengths" items={insights.positives} icon={CheckCircle2} colorClass="text-emerald-600" />
            <InsightSection title="Opportunities" items={insights.opportunities} icon={Lightbulb} colorClass="text-amber-600" />
            <InsightSection title="Segment Outliers" items={insights.segment_outliers} icon={Users} colorClass="text-blue-600" />
            <InsightSection title="Recommended Actions" items={insights.recommended_actions} icon={Zap} colorClass="text-purple-600" />
          </div>
        ) : (
          <p className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-500 text-center py-${compact ? '2' : '4'}`}>
            {compact ? 'No AI insights available.' : 'AI insights not available. Ensure EMERGENT_LLM_KEY is configured.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════
   QuestionSummaryCard
   ═══════════════════════════════════════════════════════════ */

const TYPE_CONFIG = {
  slider: { icon: SlidersHorizontal, label: 'Slider' },
  free_text: { icon: Type, label: 'Free Text' },
  multi_topic: { icon: Hash, label: 'Multi Topic' },
  multiple_choice: { icon: CheckCircle2, label: 'Multiple Choice' },
  checkbox: { icon: CheckCircle2, label: 'Checkbox' },
};

function CompactTypeBadge({ type, responseCount }) {
  const cfg = TYPE_CONFIG[type] || { icon: BarChart3, label: type };
  const Icon = cfg.icon;
  return (
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-slate-400" />
        <span className="text-[9px] font-medium text-slate-500">{cfg.label}</span>
      </div>
      <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
        <Eye className="w-2.5 h-2.5" /> n={responseCount}
      </span>
    </div>
  );
}

export function QuestionSummaryCard({ question, onClick, compact = false }) {
  const cardClass = compact
    ? 'p-2.5 bg-white rounded-lg border border-slate-200 shadow-sm'
    : 'p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors hover:shadow-sm relative';

  const TrendIndicator = ({ trend }) => {
    if (!trend || trend.change === undefined) return null;
    const isPositive = trend.change > 0;
    const isNeutral = trend.change === 0;
    return (
      <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-emerald-600' : isNeutral ? 'text-slate-400' : 'text-rose-500'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : isNeutral ? null : <TrendingDown className="w-3 h-3" />}
        <span>{isPositive ? '+' : ''}{trend.change}%</span>
      </div>
    );
  };

  const DetailsButton = () => {
    if (compact) return null;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClick?.(question); }}
        className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-slate-200 transition-colors group"
        data-testid={`question-details-btn-${question.id}`}
      >
        <svg className="w-4 h-4 text-slate-400 group-hover:text-slate-600" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="4" cy="10" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="16" cy="10" r="1.5" />
        </svg>
      </button>
    );
  };

  if (question.question_type === 'slider') {
    const scaleMax = question.scale_max || 10;
    const percentage = scaleMax ? (question.avg / scaleMax * 100) : (question.avg * 10);

    if (compact) {
      const dist = question.distribution || [];
      const maxBucket = Math.max(...dist.map(d => d.count), 1);
      return (
        <div className={cardClass} data-testid={`question-card-${question.id}`}>
          <CompactTypeBadge type="slider" responseCount={question.response_count || question.count} />
          <h4 className="text-[11px] font-bold text-slate-800 leading-tight">{question.heading}</h4>
          {question.question_text && <p className="text-[9px] text-slate-400 leading-snug mt-0.5 line-clamp-1">{question.question_text}</p>}
          <div className="flex items-end justify-between mt-1.5">
            <div className="flex items-baseline gap-0.5">
              <span className="text-lg font-bold text-slate-800">{formatScore(question.avg, scaleMax)}</span>
              <span className="text-[9px] text-slate-400">/{scaleMax}</span>
            </div>
            {question.std_dev !== undefined && <span className="text-[9px] text-slate-400 bg-slate-100 rounded px-1 py-0.5">&sigma; = {question.std_dev}</span>}
          </div>
          <div className="relative mt-1.5 h-2 rounded-full overflow-hidden" style={{ background: 'linear-gradient(90deg, #f87171 0%, #fbbf24 35%, #a3e635 65%, #22c55e 100%)' }}>
            <div className="absolute top-0 w-0.5 h-full bg-slate-800 rounded" style={{ left: `${percentage}%`, transform: 'translateX(-50%)' }} />
          </div>
          <div className="flex justify-between text-[8px] text-slate-400 mt-0.5">
            <span>{question.left_label || 'Low'}</span>
            <span>{question.right_label || 'High'}</span>
          </div>
          {dist.length > 0 && (
            <div className="flex items-end gap-px mt-1.5" style={{ height: 28 }}>
              {dist.map((b, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div className="w-full rounded-t" style={{ height: Math.max(2, (b.count / maxBucket) * 24), backgroundColor: '#5AA2AE', opacity: 0.7 + (i / dist.length) * 0.3 }} />
                  <span className="text-[7px] text-slate-400 mt-0.5">{b.range}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={cardClass} onClick={() => onClick?.(question)} data-testid={`question-card-${question.id}`}>
        <DetailsButton />
        <div className="flex items-start justify-between mb-2 pr-6">
          <h4 className="text-sm font-medium text-slate-700 flex-1 mr-2">{question.heading}</h4>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg font-bold text-[#5AA2AE]">{formatScore(question.avg, scaleMax)}</span>
            <TrendIndicator trend={question.trend} />
          </div>
        </div>
        <Progress value={percentage} className="h-2" />
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>{question.left_label || 'Low'}</span>
          <span>{question.right_label || 'High'}</span>
        </div>
        <p className="text-xs mt-2 text-slate-400">n={question.response_count}</p>
      </div>
    );
  }

  if (question.question_type === 'multi_topic' && question.top_topics) {
    const maxCount = Math.max(...question.top_topics.map(t => t.count), 1);
    const totalMentions = question.total_mentions || question.top_topics.reduce((s, t) => s + t.count, 0);
    const BARS = ['#5AA2AE', '#5AA2AE', '#5AA2AE', '#5AA2AE', '#5AA2AE'];

    if (compact) {
      return (
        <div className={cardClass} data-testid={`question-card-${question.id}`}>
          <CompactTypeBadge type="multi_topic" responseCount={question.response_count || question.count} />
          <h4 className="text-[11px] font-bold text-slate-800 leading-tight">{question.heading}</h4>
          {question.question_text && <p className="text-[9px] text-slate-400 leading-snug mt-0.5 line-clamp-1">{question.question_text}</p>}
          <div className="flex items-center gap-2 mt-1 mb-1.5">
            <span className="text-[9px] text-slate-500"><Hash className="w-2.5 h-2.5 inline" /> {totalMentions} responses</span>
            {question.unique_topics && <span className="text-[9px] text-slate-500">{question.unique_topics} unique topics</span>}
          </div>
          <div className="space-y-1">
            {question.top_topics.slice(0, 6).map((topic, i) => {
              const pct = totalMentions > 0 ? ((topic.count / question.response_count) * 100).toFixed(1) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] text-slate-600 truncate flex-1 mr-2">{topic.name}</span>
                    <span className="text-[9px] text-slate-500 font-medium whitespace-nowrap">{topic.count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full" style={{ height: 5 }}>
                    <div className="rounded-full" style={{ width: `${(topic.count / maxCount) * 100}%`, height: '100%', backgroundColor: BARS[i % BARS.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={cardClass} onClick={() => onClick?.(question)} data-testid={`question-card-${question.id}`}>
        <DetailsButton />
        <h4 className="text-sm font-medium text-slate-700 mb-3 pr-6">{question.heading}</h4>
        <div className="space-y-1.5">
          {question.top_topics.slice(0, 4).map((topic, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-0.5 text-xs">
                <span className="text-slate-600 truncate flex-1 mr-2">{topic.name}</span>
                <span className="text-slate-500 font-medium">{topic.count}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full" style={{ height: 6 }}>
                <div className="rounded-full" style={{ width: `${(topic.count / maxCount) * 100}%`, height: '100%', backgroundColor: BARS[i % BARS.length], transition: 'width 0.3s' }} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs mt-2 text-slate-400">n={question.response_count}</p>
      </div>
    );
  }

  if (question.question_type === 'free_text') {
    if (compact) {
      const themes = question.themes || [];
      const sentiment = question.sentiment_breakdown || {};
      const hasSentiment = sentiment.positive || sentiment.neutral || sentiment.concerned;
      return (
        <div className={cardClass} data-testid={`question-card-${question.id}`}>
          <CompactTypeBadge type="free_text" responseCount={question.response_count || question.count} />
          <h4 className="text-[11px] font-bold text-slate-800 leading-tight">{question.heading}</h4>
          {question.question_text && <p className="text-[9px] text-slate-400 leading-snug mt-0.5 line-clamp-1">{question.question_text}</p>}
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-[9px] text-slate-500 flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" /> {question.sample_count || question.response_count} responses</span>
            {question.avg_chars && <span className="text-[9px] text-slate-500 flex items-center gap-0.5"><Type className="w-2.5 h-2.5" /> ~{question.avg_chars} chars avg</span>}
          </div>
          {hasSentiment && (
            <div className="mt-1.5">
              <span className="text-[8px] text-slate-400">Sentiment breakdown:</span>
              <div className="flex h-2 rounded-full overflow-hidden mt-0.5">
                {sentiment.positive > 0 && <div style={{ width: `${sentiment.positive}%`, backgroundColor: '#22c55e' }} />}
                {sentiment.neutral > 0 && <div style={{ width: `${sentiment.neutral}%`, backgroundColor: '#9ca3af' }} />}
                {sentiment.concerned > 0 && <div style={{ width: `${sentiment.concerned}%`, backgroundColor: '#ef4444' }} />}
              </div>
              <div className="flex justify-between mt-0.5">
                {sentiment.positive > 0 && <span className="text-[8px] text-emerald-600">{sentiment.positive}% Positive</span>}
                {sentiment.neutral > 0 && <span className="text-[8px] text-slate-400">{sentiment.neutral}% Neutral</span>}
                {sentiment.concerned > 0 && <span className="text-[8px] text-red-500">{sentiment.concerned}% Concerned</span>}
              </div>
            </div>
          )}
          {themes.length > 0 && (
            <div className="mt-1.5">
              <span className="text-[8px] text-slate-400">Key themes identified:</span>
              <div className="space-y-0.5 mt-0.5">
                {themes.slice(0, 5).map((theme, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[9px] text-slate-600 truncate flex-1 mr-2">{theme.name || theme}</span>
                    {theme.percentage && <span className="text-[9px] text-slate-500">{theme.percentage}%</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!hasSentiment && themes.length === 0 && question.theme_summary && (
            <div className="mt-1.5 p-1.5 bg-purple-50 rounded border border-purple-100">
              <p className="text-[9px] text-purple-700 line-clamp-3"><Lightbulb className="w-2.5 h-2.5 inline mr-0.5" />{question.theme_summary}</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={cardClass} onClick={() => onClick?.(question)} data-testid={`question-card-${question.id}`}>
        <DetailsButton />
        <h4 className="text-sm font-medium text-slate-700 mb-2 pr-6">{question.heading}</h4>
        <div className="flex items-center gap-2 text-slate-500">
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm">{question.sample_count || question.response_count} responses</span>
        </div>
        {question.theme_summary && (
          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-100">
            <p className="text-xs text-purple-700 line-clamp-3"><Lightbulb className="w-3 h-3 inline mr-1" />{question.theme_summary}</p>
          </div>
        )}
        {!question.theme_summary && question.sample_responses?.length > 0 && (
          <div className="mt-2 space-y-1">
            {question.sample_responses.slice(0, 3).map((resp, i) => (
              <p key={i} className="text-xs text-slate-500 italic line-clamp-1">"{resp?.substring(0, 100)}{resp?.length > 100 ? '...' : ''}"</p>
            ))}
          </div>
        )}
        <p className="text-xs mt-2 text-slate-400">n={question.response_count}</p>
      </div>
    );
  }

  // multiple_choice and checkbox — horizontal bar chart like multi_topic
  if ((question.question_type === 'multiple_choice' || question.question_type === 'checkbox') && question.top_topics) {
    const maxCount = Math.max(...question.top_topics.map(t => t.count), 1);
    const BARS = ['#3D7A84', '#3D7A84', '#3D7A84', '#3D7A84', '#3D7A84'];

    if (compact) {
      return (
        <div className={cardClass} data-testid={`question-card-${question.id}`}>
          <CompactTypeBadge type={question.question_type} responseCount={question.response_count || question.count} />
          <h4 className="text-[11px] font-bold text-slate-800 leading-tight">{question.heading}</h4>
          {question.question_text && <p className="text-[9px] text-slate-400 leading-snug mt-0.5 line-clamp-1">{question.question_text}</p>}
          <div className="space-y-1 mt-1.5">
            {question.top_topics.slice(0, 5).map((topic, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9px] text-slate-600 truncate flex-1 mr-2">{topic.name}</span>
                  <span className="text-[9px] text-slate-500 font-medium">{topic.count}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full" style={{ height: 5 }}>
                  <div className="rounded-full" style={{ width: `${(topic.count / maxCount) * 100}%`, height: '100%', backgroundColor: BARS[i % BARS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={cardClass} onClick={() => onClick?.(question)} data-testid={`question-card-${question.id}`}>
        <DetailsButton />
        <h4 className="text-sm font-medium text-slate-700 mb-3 pr-6">{question.heading}</h4>
        <div className="space-y-1.5">
          {question.top_topics.slice(0, 4).map((topic, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-0.5 text-xs">
                <span className="text-slate-600 truncate flex-1 mr-2">{topic.name}</span>
                <span className="text-slate-500 font-medium">{topic.count}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full" style={{ height: 6 }}>
                <div className="rounded-full" style={{ width: `${(topic.count / maxCount) * 100}%`, height: '100%', backgroundColor: BARS[i % BARS.length], transition: 'width 0.3s' }} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs mt-2 text-slate-400">n={question.response_count}</p>
      </div>
    );
  }

  return null;
}
